import $ from "jquery";
import ko from "knockout";
import "flow/bindings/elementHandleBinding";
import { MaybeObservable } from "flow/interfaces/MaybeObservable";
import viewHtml from "./autoComplete.html";
import { selectId } from "flow/koHelpers/selectId";
import { selectLabel, selectPlaceholder } from "flow/koHelpers/selectParam";
import createEnabler from "flow/koHelpers/createEnablerDisabler";

export interface IAutoCompleteParams {
    autoFocus?: MaybeObservable<boolean>;
    elementId: MaybeObservable<string>;
    enable?: MaybeObservable<boolean>;
    disable?: MaybeObservable<boolean>;
    maxLength: number;
    minLength: number;
    metaPlaceholder?: MaybeObservable<string>;
    placeholder?: MaybeObservable<string>;
    selectedItem: KnockoutObservable<any>;
    sourceFunction: (term: string, response: (data: any) => void) => JQueryPromise<void>;
    term: MaybeObservable<string>;
    meta?: MaybeObservable<string>;
    text?: MaybeObservable<string>;
}

ko.components.register("ko-autocomplete", {
    viewModel: function (params: IAutoCompleteParams) {
        const id = selectId("ko_autocomplete", undefined);
        const label = selectLabel(params.meta, params.text);
        const enable = createEnabler(params.enable, params.disable);

        const defaultMinLength = 2;
        const defaultMaxLength = 400;

        const term = ko.isObservable(params.term) ? params.term : ko.observable(params.term);

        const elementId = ko.isObservable(params.elementId) ? params.elementId : ko.observable(params.elementId);
        if (params.minLength == null) {
            params.minLength = defaultMinLength;
        }
        const minLength = ko.isObservable(params.minLength) ? params.minLength : ko.observable(params.minLength);
        const isLoading = ko.observable(false);
        if (!ko.isObservable(params.selectedItem)) {
            throw new Error("selected Item needs to be observable");
        }
        const selectedItem = params.selectedItem;
        const sourceFunction = params.sourceFunction;

        let autoFocus: KnockoutObservable<boolean>;
        if (params.autoFocus == null) {
            autoFocus = ko.observable(true);
        } else {
            autoFocus = ko.isObservable(params.autoFocus) ? params.autoFocus : ko.observable(params.autoFocus);
        }

        const attributes = {
            maxLength: params.maxLength == null ? defaultMaxLength : params.maxLength,
            placeholder: selectPlaceholder(params.metaPlaceholder, params.placeholder)
        };

        const initializeAutoComplete = function (inputElement: HTMLInputElement) {
            let widget = $(inputElement).autocomplete({
                source: function (request: { term: string }, response: (data: any) => void) {
                    isLoading(true);
                    const promise = sourceFunction(request.term, response);
                    promise.always(function () {
                        isLoading(false);
                    });
                },
                select: function (_event, ui) {
                    term(ui.item.label);
                    selectedItem(ui.item);
                },
                change: function (_event, ui) {
                    if (selectedItem() !== ui.item) {
                        selectedItem(ui.item);
                    }
                },
                minLength: minLength(),
                autoFocus: autoFocus()
            });
            if (autoFocus() === true) {
                $(inputElement).trigger("focus");
            }
            ko.utils.domNodeDisposal.addDisposeCallback(inputElement, function () {
                if (widget) {
                    widget.autocomplete("destroy");
                    widget = null;
                }
            });
        };

        return {
            id: id,
            label: label,
            term: term,
            elementId: elementId,
            isLoading: isLoading,
            sourceFunction: sourceFunction,
            selectedItem: selectedItem,
            attributes: attributes,
            enable: enable,
            autoFocus: autoFocus,
            initializeAutoComplete: initializeAutoComplete
        };
    },
    template: viewHtml,
    synchronous: true
});
